import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";

export interface IntakeField {
  key: string;
  label: string;
  type: string; // "number" | "text" | "textarea" | "select" | "month_picker"
  unit?: string;
  required?: boolean;
  options?: string[];
}

interface Props {
  intakeSchema: IntakeField[];
  values: Record<string, unknown>;
  onChange?: (key: string, value: unknown) => void;
  onSave?: () => void;
  readOnly?: boolean;
  saving?: boolean;
}

export function GenericIntakeForm({
  intakeSchema,
  values,
  onChange,
  onSave,
  readOnly = false,
  saving = false,
}: Props) {
  if (!intakeSchema || intakeSchema.length === 0) return null;

  const renderField = (field: IntakeField) => {
    const value = values[field.key];
    const id = `intake-${field.key}`;

    switch (field.type) {
      case "number":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={id} className="text-sm font-medium">
              {field.label}
              {field.unit && <span className="text-muted-foreground ml-1">({field.unit})</span>}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {readOnly ? (
              <p className="text-sm py-1.5 px-3 bg-muted/50 rounded-md min-h-[36px]">
                {value != null ? `${value}${field.unit ? ` ${field.unit}` : ""}` : "—"}
              </p>
            ) : (
              <Input
                id={id}
                type="number"
                value={value != null ? String(value) : ""}
                onChange={(e) => onChange?.(field.key, e.target.value ? Number(e.target.value) : null)}
                placeholder={field.label}
              />
            )}
          </div>
        );

      case "select":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {readOnly ? (
              <p className="text-sm py-1.5 px-3 bg-muted/50 rounded-md min-h-[36px]">
                {value != null ? String(value) : "—"}
              </p>
            ) : (
              <Select
                value={value != null ? String(value) : ""}
                onValueChange={(v) => onChange?.(field.key, v)}
              >
                <SelectTrigger id={id}>
                  <SelectValue placeholder={`Vyberte ${field.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {(field.options || []).map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.key} className="space-y-1.5 col-span-full">
            <Label htmlFor={id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {readOnly ? (
              <p className="text-sm py-1.5 px-3 bg-muted/50 rounded-md min-h-[54px] whitespace-pre-wrap">
                {value != null ? String(value) : "—"}
              </p>
            ) : (
              <Textarea
                id={id}
                value={value != null ? String(value) : ""}
                onChange={(e) => onChange?.(field.key, e.target.value)}
                placeholder={field.label}
                rows={3}
              />
            )}
          </div>
        );

      default: // "text", "month_picker", etc.
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {readOnly ? (
              <p className="text-sm py-1.5 px-3 bg-muted/50 rounded-md min-h-[36px]">
                {value != null ? String(value) : "—"}
              </p>
            ) : (
              <Input
                id={id}
                type={field.type === "month_picker" ? "month" : "text"}
                value={value != null ? String(value) : ""}
                onChange={(e) => onChange?.(field.key, e.target.value)}
                placeholder={field.label}
              />
            )}
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {intakeSchema.map(renderField)}
      </div>
      {!readOnly && onSave && (
        <Button onClick={onSave} disabled={saving} size="sm" className="mt-2">
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Ukládám..." : "Uložit parametry"}
        </Button>
      )}
    </div>
  );
}
